import { Controller } from "@hotwired/stimulus";

type HtmxRequestEvent = CustomEvent<{
    elt?: Element;
    failed?: boolean;
    successful?: boolean;
    shouldSwap?: boolean;
    xhr?: XMLHttpRequest;
}>;

type ActiveSearchRequest = {
    controller: TicketSearchController;
    timeoutId: number;
    xhr: XMLHttpRequest;
};

let activeSearchRequest: ActiveSearchRequest | null = null;

export default class TicketSearchController extends Controller<HTMLFormElement> {
    static targets = ["clearButton", "input", "message", "spinner"];
    static values = {
        timeoutMs: { type: Number, default: 5000 },
    };

    declare readonly clearButtonTarget: HTMLButtonElement;
    declare readonly inputTarget: HTMLInputElement;
    declare readonly messageTarget: HTMLElement;
    declare readonly spinnerTarget: HTMLElement;
    declare readonly timeoutMsValue: number;

    private readonly handleBeforeRequest = (event: Event): void => {
        if (!this.requestBelongsToSearch(event)) {
            return;
        }

        const xhr = (event as HtmxRequestEvent).detail?.xhr;
        if (!xhr) {
            return;
        }

        this.abortActiveRequest(false);
        this.hideTimeoutMessage();
        this.showSpinner();

        const request: ActiveSearchRequest = {
            controller: this,
            xhr,
            timeoutId: window.setTimeout(() => {
                if (activeSearchRequest !== request) {
                    return;
                }
                this.abortActiveRequest(true);
            }, this.timeoutMsValue),
        };
        activeSearchRequest = request;
    };

    private readonly handleBeforeSwap = (event: Event): void => {
        const detail = (event as HtmxRequestEvent).detail;
        const xhr = detail?.xhr;
        if (!xhr || activeSearchRequest?.xhr !== xhr || activeSearchRequest.controller !== this) {
            return;
        }
        const timedOut = xhr.status === 408 || xhr.status === 504;
        if (timedOut && detail) {
            detail.shouldSwap = false;
            event.preventDefault();
        }
        this.finishActiveRequest(timedOut);
    };

    private readonly handleRefreshStatus = (event: Event): void => {
        const target = (event as CustomEvent<{ target?: Element }>).detail?.target;
        const selector = this.element.getAttribute("hx-target");
        const container = selector ? document.querySelector(selector) : null;
        if (target && container?.contains(target)) {
            if (event.type === "ihp:auto-refresh-error") {
                this.showTimeoutMessage();
            } else {
                this.hideTimeoutMessage();
            }
        }
    };

    private readonly handleAfterRequest = (event: Event): void => {
        const detail = (event as HtmxRequestEvent).detail;
        const xhr = detail?.xhr;
        if (!xhr || activeSearchRequest?.xhr !== xhr) {
            return;
        }

        const timedOut = xhr.status === 408 || xhr.status === 504;
        this.finishActiveRequest(timedOut);
    };

    private readonly handleRequestTimeout = (event: Event): void => {
        const xhr = (event as HtmxRequestEvent).detail?.xhr;
        if (xhr && activeSearchRequest?.xhr === xhr) {
            this.finishActiveRequest(true);
        }
    };

    private readonly handleRequestError = (event: Event): void => {
        const xhr = (event as HtmxRequestEvent).detail?.xhr;
        if (!xhr || activeSearchRequest?.xhr !== xhr) {
            return;
        }

        this.finishActiveRequest(xhr.status === 408 || xhr.status === 504);
    };

    connect(): void {
        this.element.addEventListener("htmx:beforeRequest", this.handleBeforeRequest);
        document.addEventListener("htmx:beforeSwap", this.handleBeforeSwap, true);
        document.addEventListener("ihp:auto-refresh-error", this.handleRefreshStatus);
        document.addEventListener("ihp:auto-refresh-update", this.handleRefreshStatus);
        this.element.addEventListener("htmx:afterRequest", this.handleAfterRequest);
        this.element.addEventListener("htmx:responseError", this.handleRequestError);
        this.element.addEventListener("htmx:sendError", this.handleRequestError);
        this.element.addEventListener("htmx:timeout", this.handleRequestTimeout);
        this.updateClearButton();
    }

    disconnect(): void {
        this.element.removeEventListener("htmx:beforeRequest", this.handleBeforeRequest);
        document.removeEventListener("htmx:beforeSwap", this.handleBeforeSwap, true);
        document.removeEventListener("ihp:auto-refresh-error", this.handleRefreshStatus);
        document.removeEventListener("ihp:auto-refresh-update", this.handleRefreshStatus);
        this.element.removeEventListener("htmx:afterRequest", this.handleAfterRequest);
        this.element.removeEventListener("htmx:responseError", this.handleRequestError);
        this.element.removeEventListener("htmx:sendError", this.handleRequestError);
        this.element.removeEventListener("htmx:timeout", this.handleRequestTimeout);

        if (activeSearchRequest?.controller === this) {
            this.abortActiveRequest(false);
        }
    }

    cancelCurrentRequest(): void {
        this.hideTimeoutMessage();
        this.updateClearButton();
        if (activeSearchRequest?.controller === this) {
            this.abortActiveRequest(false);
        } else {
            this.hideSpinner();
        }
    }

    clearSearch(): void {
        this.inputTarget.value = "";
        this.inputTarget.focus();
        this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }));
    }

    private updateClearButton(): void {
        this.clearButtonTarget.hidden = this.inputTarget.value.length === 0;
    }

    private abortActiveRequest(showTimeout: boolean): void {
        const request = activeSearchRequest;
        if (!request) {
            this.hideSpinner();
            if (showTimeout) {
                this.showTimeoutMessage();
            }
            return;
        }

        window.clearTimeout(request.timeoutId);
        activeSearchRequest = null;
        request.controller.hideSpinner();
        if (showTimeout) {
            request.controller.showTimeoutMessage();
        }

        if (request.xhr.readyState !== XMLHttpRequest.DONE) {
            request.xhr.abort();
        }
    }

    private finishActiveRequest(showTimeout: boolean): void {
        const request = activeSearchRequest;
        if (!request || request.controller !== this) {
            return;
        }

        window.clearTimeout(request.timeoutId);
        activeSearchRequest = null;
        this.hideSpinner();
        if (showTimeout) {
            this.showTimeoutMessage();
        }
    }

    private requestBelongsToSearch(event: Event): boolean {
        const trigger = (event as HtmxRequestEvent).detail?.elt;
        if (!(trigger instanceof Element)) {
            return false;
        }
        return trigger === this.element || trigger.closest("form") === this.element;
    }

    private showSpinner(): void {
        this.spinnerTarget.classList.remove("hidden");
        this.spinnerTarget.setAttribute("aria-hidden", "false");
    }

    private hideSpinner(): void {
        this.spinnerTarget.classList.add("hidden");
        this.spinnerTarget.setAttribute("aria-hidden", "true");
    }

    private showTimeoutMessage(): void {
        this.messageTarget.classList.remove("hidden");
    }

    private hideTimeoutMessage(): void {
        this.messageTarget.classList.add("hidden");
    }
}
